import { Injectable } from '@angular/core';
import * as LogRocket from 'logrocket';
import {
	BehaviorSubject,
	ReplaySubject,
	combineLatest,
	from,
	timer,
} from 'rxjs';
import {
	debounceTime,
	delay,
	distinctUntilChanged,
	retryWhen,
	tap,
} from 'rxjs/operators';

import { environment } from 'environments/environment';
import { IdentifyUserForAnalyticsCmd } from 'user/command/identify-user-for-analytics.cmd';
import { UserAnalyticsPort } from 'user/port/user-analytics.port';

@Injectable()
export class UserAnalyticsAdapter implements UserAnalyticsPort {
	private _identifyUserForAnalytics$ =
		new ReplaySubject<IdentifyUserForAnalyticsCmd>(1);
	private _isConversationShown$: BehaviorSubject<boolean> =
		new BehaviorSubject<boolean>(false);
	private static _isConversationHidden$: BehaviorSubject<boolean> =
		new BehaviorSubject<boolean>(true);

	constructor() {
		this._identifyUserForAnalytics$
			.pipe(
				tap((cmd) => UserAnalyticsAdapter._identifyUserForAnalytics(cmd)),
				retryWhen((e) => e.pipe(delay(1000)))
			)
			.subscribe();

		combineLatest([
			UserAnalyticsAdapter._isConversationHidden$,
			this._isConversationShown$,
		])
			.pipe(
				debounceTime(250),
				tap(() => {
					(window as any).Intercom('onHide', function () {
						/** Do stuff **/ (window as any).Intercom('shutdown');

						UserAnalyticsAdapter._isUserConversationHidden(true);
					});
				})
			)
			.subscribe();
	}

	private static _identifyUserForAnalytics(cmd: IdentifyUserForAnalyticsCmd) {
		if (environment.logRocketEnabled) {
			LogRocket.identify(cmd.userId, {
				name: cmd.userName,
				email: cmd.userEmailAddress,
			});
		}

		(window as any).Intercom('boot', {
			app_id: 'ha66amt1',
			name: cmd.userName,
			user_id: cmd.userId,
			user_role: cmd.userRole,
			hide_default_launcher: true,
			email: cmd.userEmailAddress,
			user_language: cmd.userLanguage,
			user_hash: cmd.userIntercomHash,
			language_override: cmd.userLanguage,
			action_color: `#${cmd.userOrganizationColor}`,
			background_color: `#${cmd.userOrganizationColor}`,
			company: { name: cmd.userOrganizationName, id: cmd.userOrganizationId },
		});
	}

	public identifyUserForAnalytics(cmd: IdentifyUserForAnalyticsCmd) {
		this.checkLanguageUpdateForAnalytics(cmd);

		this._identifyUserForAnalytics$.next(cmd);
	}

	public checkLanguageUpdateForAnalytics(cmd: IdentifyUserForAnalyticsCmd) {
		UserAnalyticsAdapter._isConversationHidden$
			.pipe(
				tap((isHidden) => {
					UserAnalyticsAdapter._resetIntercom(cmd);

					if (!isHidden) {
						(window as any).Intercom('show');

						this._isConversationShown$.next(true);
					}
				}),
				distinctUntilChanged()
			)
			.subscribe();
	}

	public markUserConversationAsShown() {
		this._isConversationShown$.next(true);
		UserAnalyticsAdapter._isUserConversationHidden(false);
	}

	private static _isUserConversationHidden(isConversationShown: boolean) {
		this._isConversationHidden$.next(isConversationShown);
	}

	private static _resetIntercom(cmd: IdentifyUserForAnalyticsCmd) {
		(window as any).Intercom('shutdown');

		(window as any).Intercom('boot', {
			app_id: 'ha66amt1',
			name: cmd.userName,
			user_id: cmd.userId,
			user_role: cmd.userRole,
			hide_default_launcher: true,
			email: cmd.userEmailAddress,
			user_language: cmd.userLanguage,
			user_hash: cmd.userIntercomHash,
			language_override: cmd.userLanguage,
			action_color: `#${cmd.userOrganizationColor}`,
			background_color: `#${cmd.userOrganizationColor}`,
			company: { name: cmd.userOrganizationName, id: cmd.userOrganizationId },
		});
	}
}
